import React from 'react';
import { WebsiteTab } from './WebsiteTab';
import { SavedPropertiesTab } from './SavedPropertiesTab';
import { HomeValuationsTab, type AssignedAddress } from './HomeValuationsTab';
import { useContactWebActivityStatsGQL } from '../../../../hooks/graphql/contacts/useContactWebActivityStatsGQL';
import { useContactSavedPropertiesGQL } from '../../../../hooks/graphql/contacts/useContactSavedPropertiesGQL';
import { useContactValuationsGQL } from '../../../../hooks/graphql/contacts/useContactValuationsGQL';
import { VISIT_EVENT_TYPES } from './webActivityEventTypes';
import {
  isPreviewContactId,
  PREVIEW_WEB_ACTIVITY,
  PREVIEW_SAVED_PROPERTIES,
  PREVIEW_VALUATIONS,
} from '../../../../pages/previewMockData';

interface WebsiteActivityTabProps {
  contactId: string;
  firebaseUid?: string;
  onAddressAssigned?: (address: AssignedAddress) => void;
}

const Stat: React.FC<{ value: number; label: string; loading?: boolean }> = ({
  value,
  label,
  loading,
}) => (
  <div className="flex flex-col min-w-0 px-4 py-3.5 gap-1">
    {loading ? (
      // Sized to the number's line box so the count filling in leaves the
      // label where it already was.
      <div className="h-8 w-10 rounded bg-foreground/10 animate-pulse" aria-hidden />
    ) : (
      <span className="text-2xl font-bold text-foreground tabular-nums">{value ?? 0}</span>
    )}
    <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</span>
  </div>
);

/**
 * Website activity — stats row on top (times visited / saved properties
 * / searches), then the three live tab tables stacked on one scrollable
 * page. Each inner tab keeps its own ActivityTableHeader title and
 * filter cluster, so this wrapper stays chrome-free.
 */
export const WebsiteActivityTab: React.FC<WebsiteActivityTabProps> = ({
  contactId,
  firebaseUid,
  onAddressAssigned,
}) => {
  const isPreview = isPreviewContactId(contactId);

  // Times visited / property views / searches come from one tenant-scoped
  // aggregate, narrowed to the same event types shown by the Website Activity
  // table so the total can be reconciled with the rows below it. Property
  // views and searches are subsets of that set.
  // Saved properties and valuations keep one parent-owned list query each so
  // their counters and tables share the same observer and pagination state.
  const { stats, loading: statsLoading } = useContactWebActivityStatsGQL(
    isPreview ? '' : contactId,
    VISIT_EVENT_TYPES
  );
  const saved = useContactSavedPropertiesGQL(isPreview ? '' : contactId);
  const valuations = useContactValuationsGQL(isPreview ? '' : contactId);

  const timesVisitedTotal = isPreview
    ? PREVIEW_WEB_ACTIVITY.filter((e: { eventType: string }) =>
        (VISIT_EVENT_TYPES as readonly string[]).includes(e.eventType)
      ).length
    : stats.total;
  const propertyViewsTotal = isPreview
    ? PREVIEW_WEB_ACTIVITY.filter((e: { eventType: string }) => e.eventType === 'property_view')
        .length
    : stats.propertyViews;
  const searchesTotal = isPreview
    ? PREVIEW_WEB_ACTIVITY.filter((e: { eventType: string }) => e.eventType === 'search').length
    : stats.searches;
  const savedTotal = isPreview ? PREVIEW_SAVED_PROPERTIES.length : saved.total;
  const valuationsTotal = isPreview ? PREVIEW_VALUATIONS.length : valuations.total;

  // Preview data is local, so it never has a pending pass. For the live lists a
  // zero total is the only way to tell a first load from a background refetch.
  const statsPending = !isPreview && statsLoading;
  const savedPending = !isPreview && saved.loading && saved.total === 0;
  const valuationsPending = !isPreview && valuations.loading && valuations.total === 0;

  return (
    <div className="flex flex-col flex-grow min-h-0 overflow-y-auto scrollbar-custom -mx-3 md:-mx-4 lg:-mx-5 px-3 md:px-4 lg:px-5 pb-4">
      <div className="flex flex-col gap-6">
        {/* Stats row — one bordered container, 5 plain number+label cells
            side by side. Times visited counts the events shown in the Website
            Activity table; Property views and Searches are subsets of it. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 rounded-xl border border-foreground/30 divide-x divide-foreground/15 bg-white dark:bg-card flex-shrink-0 shadow-[0_4px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.35)]">
          <Stat value={timesVisitedTotal} label="Times visited" loading={statsPending} />
          <Stat value={propertyViewsTotal} label="Property views" loading={statsPending} />
          <Stat value={savedTotal} label="Saved properties" loading={savedPending} />
          <Stat value={searchesTotal} label="Searches" loading={statsPending} />
          <Stat value={valuationsTotal} label="Home valuations" loading={valuationsPending} />
        </div>

        <WebsiteTab contactId={contactId} firebaseUid={firebaseUid} isActive={true} />
        <SavedPropertiesTab contactId={contactId} savedProperties={saved} isActive={true} />
        <HomeValuationsTab
          contactId={contactId}
          valuations={valuations}
          firebaseUid={firebaseUid}
          isActive={true}
          onAddressAssigned={onAddressAssigned}
        />
      </div>
    </div>
  );
};
